import { TRANSLATION_REGISTRY, ComponentPath, Language } from "./i18n-registry";

type TranslationLoader = () => Promise<any>;
type ComponentTranslations = Record<string, TranslationLoader>;

/**
 * Load translations for a component
 * @param language - Language code (e.g., 'en', 'es', 'pt-BR')
 * @param componentPath - Component path (e.g., 'components/auth')
 * @returns Promise with translations object
 */
export const loadSingleTranslation = async (
  language: Language | string,
  componentPath: ComponentPath
): Promise<Record<string, any>> => {
  try {
    const component = TRANSLATION_REGISTRY[
      componentPath
    ] as ComponentTranslations;
    if (!component) {
      throw new Error(
        `Component "${componentPath}" not found in translation registry`
      );
    }

    // Try requested language first
    let loader: TranslationLoader | undefined = component[language];

    // Fallback to English if language not found
    if (!loader) {
      console.warn(
        `Language "${language}" not found for "${componentPath}", falling back to English`
      );
      loader = component["en"];
    }

    return await loader();
  } catch (error) {
    console.error(
      `Error loading translations for ${componentPath} (${language}):`,
      error
    );

    // Final fallback: try to load English from first available component
    try {
      const componentKeys = Object.keys(
        TRANSLATION_REGISTRY
      ) as ComponentPath[];
      if (componentKeys.length > 0) {
        const firstComponent = TRANSLATION_REGISTRY[
          componentKeys[0]
        ] as ComponentTranslations;
        const fallbackLoader = firstComponent["en"];

        if (fallbackLoader) {
          const fallbackTranslations = await fallbackLoader();
          console.warn(
            `Loaded fallback English translations instead of ${componentPath}`
          );
          return fallbackTranslations.default || fallbackTranslations;
        }
      }
    } catch (fallbackError) {
      console.error("Failed to load fallback translations:", fallbackError);
    }

    // Return empty object to prevent crashes
    return {};
  }
};

export const loadTranslations = async (
  language: Language | string,
  componentPath: ComponentPath | ComponentPath[]
): Promise<Record<string, any>> => {
  // Handle single path (backward compatible)
  if (typeof componentPath === "string") {
    return loadSingleTranslation(language, componentPath);
  }

  // Handle array of paths
  if (Array.isArray(componentPath)) {
    const translationPromises = componentPath.map((path) =>
      loadSingleTranslation(language, path)
    );

    const translationsArray = await Promise.all(translationPromises);

    // Merge all translations into a single object
    return translationsArray.reduce((acc, translations) => {
      return { ...acc, ...translations };
    }, {});
  }
  return {};
};

/**
 * Preload translations for a component in multiple languages
 * Useful for components that need immediate translation access
 */
export const preloadTranslations = async (
  componentPath: ComponentPath,
  languages: Language[]
): Promise<Partial<Record<Language, Record<string, any>>>> => {
  const results: Partial<Record<Language, Record<string, any>>> = {};

  await Promise.allSettled(
    languages.map(async (lang) => {
      try {
        results[lang] = await loadSingleTranslation(lang, componentPath);
      } catch (error) {
        console.warn(`Failed to preload ${lang} for ${componentPath}`, error);
      }
    })
  );

  return results;
};

/**
 * Check if a language is available for a component
 */
export const isLanguageAvailable = (
  language: Language,
  componentPath: ComponentPath
): boolean => {
  try {
    const component = TRANSLATION_REGISTRY[
      componentPath
    ] as ComponentTranslations;
    return !!component && !!component[language];
  } catch {
    return false;
  }
};

/**
 * Get all available languages for a component
 */
export const getAvailableLanguages = (
  componentPath: ComponentPath
): Language[] => {
  try {
    const component = TRANSLATION_REGISTRY[
      componentPath
    ] as ComponentTranslations;
    if (!component) return [];

    return Object.keys(component).filter(
      (key) => typeof component[key] === "function"
    ) as Language[];
  } catch {
    return [];
  }
};

/**
 * Get all available component paths
 */
export const getAvailableComponents = (): ComponentPath[] => {
  return Object.keys(TRANSLATION_REGISTRY) as ComponentPath[];
};

/**
 * Synchronously get a translation loader (without executing it)
 * Useful for checking if a translation exists
 */
export const getTranslationLoader = (
  language: Language,
  componentPath: ComponentPath
): TranslationLoader | null => {
  try {
    const component = TRANSLATION_REGISTRY[
      componentPath
    ] as ComponentTranslations;
    return component?.[language] || component?.["en"] || null;
  } catch {
    return null;
  }
};
